"use client";

import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import type { Language } from "@/i18n/languages";

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);
const LANGUAGE_KEY = "sa-language";

function readLanguageFromCookie() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(^|; )${LANGUAGE_KEY}=([^;]+)`));
  return (match?.[2] as Language | undefined) ?? null;
}

function persistLanguage(language: Language) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = language;
  window.localStorage.setItem(LANGUAGE_KEY, language);
  document.cookie = `${LANGUAGE_KEY}=${language}; path=/; max-age=31536000; samesite=lax`;
}

function getInitialLanguage(): Language {
  if (typeof window === "undefined") return "en";
  const params = new URLSearchParams(window.location.search);
  const urlLang = (params.get("lang") as Language) ?? null;
  const storedLang = window.localStorage.getItem(LANGUAGE_KEY) as Language | null;
  const cookieLang = readLanguageFromCookie();
  const htmlLang = (document.documentElement.lang as Language) ?? null;
  return urlLang || cookieLang || storedLang || htmlLang || "en";
}

function subscribeToLanguageChange(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {};

  const handleChange = () => onStoreChange();
  window.addEventListener("storage", handleChange);
  window.addEventListener("popstate", handleChange);

  return () => {
    window.removeEventListener("storage", handleChange);
    window.removeEventListener("popstate", handleChange);
  };
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const browserLanguage = useSyncExternalStore<Language>(
    subscribeToLanguageChange,
    getInitialLanguage,
    () => "en",
  );
  const [languageOverride, setLanguageOverride] = useState<Language | null>(null);
  const language = languageOverride ?? browserLanguage;

  useEffect(() => {
    persistLanguage(language);
  }, [language]);

  const setLanguage = (nextLanguage: Language) => {
    setLanguageOverride(nextLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguageOptional() {
  return useContext(LanguageContext) ?? null;
}

export function useLanguage() {
  const context = useLanguageOptional();
  if (context) return context;

  if (process.env.NODE_ENV !== "production") {
    throw new Error("useLanguage must be used within LanguageProvider");
  }

  return {
    language: "en" as Language,
    setLanguage() {
      // noop in production fallback
    },
  };
}
